import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

interface Service {
  _id: string;
  serviceName: string;
}

interface BookingNotificationSettings {
  enabled: boolean;
  services: Record<string, string>;
}

interface BookingNotificationControlProps {
  label: string;
  value: BookingNotificationSettings;
  isDarkMode?: boolean;
  availableServices: Service[];
  onChange: (value: BookingNotificationSettings) => void;
}

export function BookingNotificationControl({
  label,
  value,
  isDarkMode = false,
  availableServices,
  onChange,
}: BookingNotificationControlProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedCount = Object.keys(value.services).length;

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 320,
        width: 320,
      });
    }
  }, [isOpen]);

  const getSummaryText = () => {
    if (!value.enabled) return "Never";
    if (selectedCount === 0) return "No services selected";
    return `${selectedCount} service${selectedCount !== 1 ? "s" : ""}`;
  };

  const updateServiceScope = (serviceId: string, scope: "0" | "1" | null) => {
    const newServices = { ...value.services };

    if (scope === null) {
      delete newServices[serviceId];
    } else {
      newServices[serviceId] = scope;
    }

    onChange({
      ...value,
      services: newServices,
    });
  };

  const toggleSelectAll = () => {
    if (selectedCount === availableServices.length) {
      onChange({
        ...value,
        services: {},
      });
    } else {
      const allServices: Record<string, string> = {};
      availableServices.forEach((service) => {
        allServices[service._id] = "1"; // Default to "all-bookings"
      });
      onChange({
        ...value,
        services: allServices,
      });
    }
  };

  return (
    <div className="flex items-center justify-between">
      <Label>{label}</Label>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`w-[280px] justify-between ${
              isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""
            }`}
          >
            <span className="truncate">{getSummaryText()}</span>
            <ChevronDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          sideOffset={8}
          className={`w-[280px] p-0 rounded-md shadow-2xl ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
            {/* Enable notifications */}
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Enable Notifications</Label>
              <Switch
                checked={value.enabled}
                onCheckedChange={(checked) => {
                  if (!checked) {
                    onChange({ enabled: false, services: {} });
                  } else {
                    onChange({ ...value, enabled: checked });
                  }
                }}
              />
            </div>

            {value.enabled && (
              <div className="space-y-2">
                {/* Services header */}
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Services</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelectAll();
                    }}
                  >
                    {selectedCount === availableServices.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </div>

                {/* Services list */}
                <div
                  className={`border rounded-md max-h-[300px] overflow-y-auto ${
                    isDarkMode ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  {availableServices.map((service) => {
                    const currentScope = value.services[service._id];
                    const isEnabled = currentScope !== undefined;

                    return (
                      <div
                        key={service._id}
                        className={`p-3 border-b last:border-b-0 ${
                          isDarkMode ? "border-gray-600" : "border-gray-100"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <Checkbox
                            checked={isEnabled}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                updateServiceScope(service._id, "1");
                              } else {
                                updateServiceScope(service._id, null);
                              }
                            }}
                            className="mt-1"
                          />

                          <div className="flex-1 space-y-2">
                            <Label className="block text-sm font-medium cursor-pointer">
                              {service.serviceName}
                            </Label>

                            {isEnabled && (
                              <RadioGroup
                                value={currentScope}
                                onValueChange={(newScope: "0" | "1") =>
                                  updateServiceScope(service._id, newScope)
                                }
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="0" />
                                  <Label className="text-xs font-normal cursor-pointer">
                                    Only mine
                                  </Label>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="1" />
                                  <Label className="text-xs font-normal cursor-pointer">
                                    All bookings
                                  </Label>
                                </div>
                              </RadioGroup>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
